import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { META, getStats, getGoal, getTodayCount, readinessLabel, getCourse, getNewlyEarnedBadges } from "../lib/store";
import { BadgesGrid } from "../components/Badges";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  Cell,
  LineChart,
  Line,
  YAxis,
} from "recharts";
import {
  Flame,
  Target,
  CalendarClock,
  ArrowRight,
  BookOpen,
  Layers,
  ClipboardList,
  GraduationCap,
  Compass,
  Trophy,
  Star,
  CloudUpload,
  LogIn,
  X,
  Sparkles,
  RadioTower,
} from "lucide-react";

function LoginBanner() {
  const { user, loading, login } = useAuth();
  const [hidden, setHidden] = useState(() => localStorage.getItem("hamprep_hide_login_banner") === "1");
  if (loading || user || hidden) return null;
  const dismiss = () => {
    localStorage.setItem("hamprep_hide_login_banner", "1");
    setHidden(true);
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="login-banner"
      className="relative flex flex-col sm:flex-row sm:items-center gap-3 rounded-sm border border-primary/30 bg-primary/10 p-4 pr-10"
    >
      <span className="grid place-items-center w-10 h-10 rounded-sm bg-primary/15 text-primary shrink-0">
        <CloudUpload size={20} />
      </span>
      <div className="flex-1">
        <div className="font-display font-bold text-sm">Connectez-vous pour sauvegarder votre progression</div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Sans compte, votre progression reste sur cet appareil uniquement. Connectez-vous pour la synchroniser et la retrouver partout.
        </p>
      </div>
      <button
        onClick={login}
        data-testid="banner-login-btn"
        className="inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:-translate-y-px transition-transform shrink-0"
      >
        <LogIn size={16} /> Se connecter
      </button>
      <button onClick={dismiss} data-testid="banner-dismiss" aria-label="Fermer" className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors">
        <X size={16} />
      </button>
    </motion.div>
  );
}

const fade = {
  hidden: { opacity: 0, y: 14 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05 } }),
};

function Stat({ icon: Icon, value, label, accent, to, i }) {
  const inner = (
    <>
      <Icon size={22} style={{ color: accent }} />
      <div>
        <div className="font-mono text-3xl font-bold tabular tracking-tight">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
      </div>
    </>
  );
  return (
    <motion.div custom={i} variants={fade} initial="hidden" animate="show" data-testid={`stat-${label}`}>
      {to ? (
        <Link to={to} className="card-brutal rounded-sm border border-border bg-card p-5 flex flex-col gap-3 h-full transition-[box-shadow,transform]">
          {inner}
        </Link>
      ) : (
        <div className="rounded-sm border border-border bg-card p-5 flex flex-col gap-3 h-full">{inner}</div>
      )}
    </motion.div>
  );
}

function GoalRing({ value, goal }) {
  const pct = Math.min(100, Math.round((value / goal) * 100));
  const R = 46;
  const C = 2 * Math.PI * R;
  const off = C - (pct / 100) * C;
  const reached = value >= goal;
  return (
    <div className="flex items-center gap-5">
      <div className="relative w-28 h-28 shrink-0">
        <svg viewBox="0 0 110 110" className="w-full h-full -rotate-90">
          <circle cx="55" cy="55" r={R} fill="none" stroke="hsl(var(--muted))" strokeWidth="9" />
          <circle
            cx="55" cy="55" r={R} fill="none"
            stroke={reached ? "hsl(var(--success))" : "hsl(var(--primary))"}
            strokeWidth="9" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={off}
            style={{ transition: "stroke-dashoffset 0.6s ease" }}
          />
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <span className="font-mono text-2xl font-bold tabular">{value}</span>
        </div>
      </div>
      <div>
        <div className="font-display font-bold text-lg">Objectif du jour</div>
        <p className="text-sm text-muted-foreground mt-1">
          {reached ? "Objectif atteint, bravo ! 🎯" : `Encore ${goal - value} question${goal - value > 1 ? "s" : ""} pour atteindre ${goal}.`}
        </p>
        <Link to="/reglages" className="text-xs text-primary hover:underline mt-2 inline-block">Modifier l'objectif →</Link>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { level } = useApp();
  const cfg = META[level];
  const stats = useMemo(() => getStats(level), [level]);
  const goal = getGoal();
  const today = getTodayCount(level);
  const bestExam = stats.exams.length ? Math.max(...stats.exams.map((e) => e.percent)) : null;

  const chartData = stats.sections.map((s) => ({
    name: s.code.replace(/^[AB]-0*/, "§"),
    pct: s.total ? Math.round((s.mastered / s.total) * 100) : 0,
  }));
  const examTrend = stats.exams.slice().reverse().map((e, i) => ({ n: i + 1, pct: e.percent }));

  const courseDone = getCourse(level).length;
  const totalModules = cfg.sections.length;
  const coursePct = Math.round((courseDone / totalModules) * 100);

  useEffect(() => {
    getNewlyEarnedBadges(level).forEach((b) => toast.success(`Badge débloqué : ${b.label} !`, { description: b.desc }));
  }, [level]);

  const modules = [
    { to: "/reviser", icon: CalendarClock, title: "Réviser (répétition espacée)", desc: `${stats.dueCount} question${stats.dueCount > 1 ? "s" : ""} à revoir aujourd'hui.` },
    { to: "/etudier", icon: BookOpen, title: "Étudier par section", desc: "Répondez et validez pour voir la correction." },
    { to: "/cartes", icon: Layers, title: "Cartes mémoires", desc: "Révisez en retournant les cartes." },
    { to: "/quiz", icon: ClipboardList, title: "Quiz par thème", desc: "Testez une section avec retour immédiat." },
    { to: "/examen", icon: GraduationCap, title: "Examen blanc", desc: `${cfg.examQuestions} questions · ${cfg.durationMin} min · ${cfg.passMark}%.` },
  ];

  return (
    <div className="space-y-8">
      <LoginBanner />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-sm bg-primary/15 text-primary px-2.5 py-1 font-mono text-xs uppercase tracking-[0.15em]">
            <RadioTower size={13} /> Qualification {cfg.label}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-3">Tableau de bord</h1>
          <p className="text-muted-foreground mt-2 max-w-xl text-sm">{cfg.description}</p>
        </div>
        <Link to="/examen" data-testid="cta-start-exam" className="inline-flex items-center gap-2 rounded-sm border border-border px-5 py-3 font-semibold text-sm hover:bg-secondary transition-colors">
          Examen blanc <ArrowRight size={16} />
        </Link>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <Link
          to="/formation"
          data-testid="module-/formation"
          className="group relative block overflow-hidden rounded-sm border-2 border-primary/50 bg-primary/10 p-6 lg:p-8 hover:-translate-y-0.5 transition-transform"
        >
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-primary/15 blur-2xl" />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
            <span className="grid place-items-center w-16 h-16 rounded-sm bg-primary text-primary-foreground shrink-0">
              <Compass size={32} />
            </span>
            <div className="flex-1">
              <div className="inline-flex items-center gap-1.5 rounded-sm bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">
                <Sparkles size={11} /> Commencez ici
              </div>
              <h2 className="font-display font-black text-2xl sm:text-3xl mt-2">Parcours de formation guidé</h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Le meilleur point de départ : un cours interactif, module par module, qui vous enseigne toute la théorie avant de valider par des questions. Idéal pour partir de zéro.
              </p>
              <div className="mt-4 max-w-md">
                <div className="flex justify-between text-xs font-mono text-muted-foreground mb-1">
                  <span>{courseDone}/{totalModules} modules complétés</span>
                  <span>{coursePct}%</span>
                </div>
                <div className="h-2 rounded-sm bg-background/60 overflow-hidden">
                  <div className="h-full bg-primary transition-[width]" style={{ width: `${coursePct}%` }} />
                </div>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-5 py-3 font-semibold text-sm shrink-0 group-hover:gap-3 transition-all">
              {courseDone > 0 ? "Continuer" : "Commencer"} <ArrowRight size={16} />
            </span>
          </div>
        </Link>
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat i={0} icon={Target} value={`${stats.progress}%`} label="Maîtrise globale" accent="hsl(var(--primary))" />
        <Stat i={1} icon={Flame} value={stats.streak} label="Jours consécutifs" accent="hsl(var(--warning))" />
        <Stat i={2} icon={CalendarClock} value={stats.dueCount} label="À réviser" accent="hsl(var(--primary))" to="/reviser" />
        <Stat i={3} icon={Star} value={stats.favorites} label="Favoris" accent="hsl(var(--warning))" to="/favoris" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <motion.div variants={fade} custom={4} initial="hidden" animate="show" className="lg:col-span-5 rounded-sm border border-border bg-card p-6">
          <GoalRing value={today} goal={goal} />
        </motion.div>
        <motion.div variants={fade} custom={5} initial="hidden" animate="show" className="lg:col-span-7 rounded-sm border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg">Évolution des examens</h2>
            {bestExam !== null && (
              <span className="inline-flex items-center gap-1.5 font-mono text-xs text-warning"><Trophy size={14} /> Record {bestExam}%</span>
            )}
          </div>
          <div className="h-48" data-testid="exam-trend">
            {examTrend.length >= 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={examTrend} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                  <XAxis dataKey="n" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} formatter={(v) => [`${v}%`, "Score"]} labelFormatter={(l) => `Examen ${l}`} />
                  <Line type="monotone" dataKey="pct" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3, fill: "hsl(var(--primary))" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-muted-foreground text-center">
                Passez un examen blanc pour voir votre progression ici.
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <motion.div variants={fade} custom={6} initial="hidden" animate="show" className="lg:col-span-7 rounded-sm border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg">Maîtrise par section</h2>
            <span className="font-mono text-xs text-muted-foreground">% maîtrisé</span>
          </div>
          <div className="h-56" data-testid="section-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 0, left: -28, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} formatter={(v) => [`${v}%`, "Maîtrise"]} />
                <Bar dataKey="pct" radius={[2, 2, 0, 0]}>
                  {chartData.map((d) => (
                    <Cell key={d.name} fill={d.pct >= 70 ? "hsl(var(--success))" : d.pct > 0 ? "hsl(var(--warning))" : "hsl(var(--muted))"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div variants={fade} custom={7} initial="hidden" animate="show" className="lg:col-span-5 rounded-sm border border-border bg-card p-6">
          <h2 className="font-display font-bold text-lg mb-4">Préparation par section</h2>
          <ul className="space-y-2" data-testid="readiness-list">
            {stats.sections.map((s) => {
              const pct = s.total ? Math.round((s.mastered / s.total) * 100) : 0;
              const r = readinessLabel(pct);
              const tone = r.tone === "success" ? "bg-success/15 text-success" : r.tone === "warning" ? "bg-warning/15 text-warning" : "bg-muted text-muted-foreground";
              return (
                <li key={s.code} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate"><span className="font-mono text-xs text-primary mr-1">{s.code}</span>{s.name}</span>
                  <span className={`font-mono text-[11px] px-2 py-0.5 rounded-sm shrink-0 ${tone}`}>{r.label}</span>
                </li>
              );
            })}
          </ul>
        </motion.div>
      </div>

      <motion.div variants={fade} custom={8} initial="hidden" animate="show">
        <BadgesGrid level={level} />
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {modules.map((m, idx) => (
          <motion.div key={m.to} variants={fade} custom={8 + idx} initial="hidden" animate="show">
            <Link to={m.to} data-testid={`module-${m.to.slice(1)}`} className="card-brutal group flex items-start gap-4 rounded-sm border border-border bg-card p-5 transition-[box-shadow,transform]">
              <span className="grid place-items-center w-11 h-11 rounded-sm bg-primary/10 text-primary shrink-0">
                <m.icon size={22} />
              </span>
              <div>
                <div className="font-display font-bold text-lg flex items-center gap-2">
                  {m.title}
                  <ArrowRight size={16} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[transform,opacity]" />
                </div>
                <div className="text-sm text-muted-foreground mt-1">{m.desc}</div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
